/**
 * Component containing all of the individual nav links for logged-in users
 */
import React from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import classnames from 'classnames';
import { withStyles } from '@material-ui/styles';
import Badge from '@material-ui/core/Badge';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import AddCircle from '@material-ui/icons/AddCircle';
import ExitToApp from '@material-ui/icons/ExitToApp';
import Dashboard from '@material-ui/icons/Dashboard';
import Assignment from '@material-ui/icons/Assignment';
import Group from '@material-ui/icons/Group';
import GroupWork from '@material-ui/icons/GroupWork';
import MailOutline from '@material-ui/icons/MailOutline';
import Help from '@material-ui/icons/Help';
import AccountCircle from '@material-ui/icons/AccountCircle';
import OpenInNew from '@material-ui/icons/OpenInNew';
import Description from '@material-ui/icons/Description';
import TransitEnterexit from '@material-ui/icons/TransitEnterexit';
import Warning from '@material-ui/icons/Warning';

import RBACWrapper from 'components/RBACWrapper/RBACWrapperContainer';
import SegmentClickTracker from 'components/SegmentClickTracker/SegmentClickTrackerContainer';
import FlagEnabledContainer from 'components/FlagEnabled/FlagEnabledContainer';
import {
  OWNER,
  ADMIN,
  METRICS,
  READONLY,
  OR_ADMIN,
  OR_READONLY,
  OR_SUPERUSER,
  OR_SUPERREAD,
  BILLING,
} from 'constants/roles';

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  className: PropTypes.string,
  isAuthenticated: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  handleNavToggle: PropTypes.func.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  createString: PropTypes.string.isRequired,
};

const defaultProps = {
  className: '',
};

export function UnstyledNavItems({
  classes,
  className,
  isAuthenticated,
  isAdmin,
  handleNavToggle,
  location,
  createString,
}) {
  return (
    <List
      role="navigation"
      className={classnames(className, classes.root)}
      onClick={() => handleNavToggle(false)}
    >
      <FlagEnabledContainer flagKey="or-internal">
        <SegmentClickTracker buttonName="Dashboard (Nav Bar)">
          <ListItem
            component={NavLink}
            classes={{
              root: classes.link,
              divider: classes.divider,
              selected: classes.selectedLink,
            }}
            to="/mission-ctrl/dashboard"
            button
            divider
            selected={location.pathname === '/mission-ctrl/dashboard'}
          >
            <ListItemIcon className={classes.icon}>
              <Dashboard />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ className: classes.itemTextTypography }}>
              Dashboard
            </ListItemText>
          </ListItem>
        </SegmentClickTracker>
      </FlagEnabledContainer>
      <RBACWrapper
        roles={[OWNER, ADMIN, READONLY, BILLING, OR_ADMIN, OR_READONLY, OR_SUPERUSER, OR_SUPERREAD]}
      >
        <SegmentClickTracker buttonName="Instances (Nav Bar)">
          <ListItem
            component={NavLink}
            classes={{
              root: classes.link,
              divider: classes.divider,
              selected: classes.selectedLink,
            }}
            to="/mission-ctrl/instances"
            exact
            button
            divider
            selected={location.pathname === '/mission-ctrl/instances'}
          >
            <ListItemIcon className={classes.icon}>
              <GroupWork />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ className: classes.itemTextTypography }}>
              Instances
            </ListItemText>
          </ListItem>
        </SegmentClickTracker>
      </RBACWrapper>
      <SegmentClickTracker buttonName="Create Instance (Nav Bar)">
        <ListItem
          component={NavLink}
          classes={{
            root: classes.link,
            divider: classes.divider,
            selected: classes.selectedLink,
          }}
          to={createString}
          button
          divider
          selected={location.pathname === createString}
        >
          <ListItemIcon className={classnames(classes.icon, classes.orange)}>
            <AddCircle />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ className: classes.itemTextTypography }}>
            Create Instance
          </ListItemText>
        </ListItem>
      </SegmentClickTracker>
      <RBACWrapper
        roles={[OWNER, ADMIN, METRICS, READONLY, OR_ADMIN, OR_READONLY, OR_SUPERUSER, OR_SUPERREAD]}
      >
        <SegmentClickTracker buttonName="Grafana Metrics (Nav Bar)">
          <ListItem
            component="a"
            classes={{
              root: classes.link,
              divider: classes.divider,
              selected: classes.selectedLink,
            }}
            target="_blank"
            rel="noopener noreferrer"
            href={process.env.REACT_APP_GRAFANA_DASHBOARD_URL}
            button
            divider
          >
            <ListItemIcon className={classnames(classes.icon)}>
              <img alt="grafana icon" height="24px" width="24px" src="/images/logos/grafana.svg" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ className: classes.itemTextTypography }}>
              Grafana Metrics
            </ListItemText>
          </ListItem>
        </SegmentClickTracker>
      </RBACWrapper>
      <FlagEnabledContainer flagKey="or-internal">
        <SegmentClickTracker buttonName="Pricing (Nav Bar)">
          <ListItem
            component={NavLink}
            classes={{
              root: classes.link,
              divider: classes.divider,
              selected: classes.selectedLink,
            }}
            to="/mission-ctrl/plans"
            button
            divider
            selected={location.pathname === '/mission-ctrl/plans'}
          >
            <ListItemIcon className={classes.icon}>
              <Assignment />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ className: classes.itemTextTypography }}>
              Pricing
            </ListItemText>
          </ListItem>
        </SegmentClickTracker>
        <SegmentClickTracker buttonName="Messages (Nav Bar)">
          <ListItem
            component={NavLink}
            classes={{
              root: classes.link,
              divider: classes.divider,
              selected: classes.selectedLink,
            }}
            to="/mission-ctrl/messages"
            button
            divider
            selected={location.pathname === '/mission-ctrl/messages'}
          >
            <ListItemIcon className={classes.icon}>
              <Badge classes={{ badge: classes.badge }} badgeContent="3" color="secondary">
                <MailOutline className={classes.icon} />
              </Badge>
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ className: classes.itemTextTypography }}>
              Messages
            </ListItemText>
          </ListItem>
        </SegmentClickTracker>
      </FlagEnabledContainer>
      <RBACWrapper roles={[OWNER, OR_ADMIN, OR_SUPERUSER]}>
        <SegmentClickTracker buttonName="Users (Nav Bar)">
          <ListItem
            component={NavLink}
            classes={{
              root: classes.link,
              divider: classes.divider,
              selected: classes.selectedLink,
            }}
            to="/mission-ctrl/users"
            button
            divider
            selected={location.pathname === '/mission-ctrl/users'}
          >
            <ListItemIcon className={classes.icon}>
              <Group />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ className: classes.itemTextTypography }}>
              Users
            </ListItemText>
          </ListItem>
        </SegmentClickTracker>
      </RBACWrapper>
      <SegmentClickTracker buttonName="Profile Management (Nav Bar)">
        <ListItem
          component={NavLink}
          className={classes.profile}
          classes={{
            root: classes.link,
            divider: classes.divider,
            selected: classes.selectedLink,
          }}
          to="/mission-ctrl/profile"
          button
          divider
          selected={location.pathname === '/mission-ctrl/profile'}
        >
          <ListItemIcon className={classes.icon}>
            <AccountCircle />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ className: classes.itemTextTypography }}>
            Profile Management
          </ListItemText>
        </ListItem>
      </SegmentClickTracker>
      <SegmentClickTracker buttonName="ORv4 SSO (Nav Bar)">
        <ListItem
          component="a"
          href={process.env.REACT_APP_ORV4_SSO_LOGIN}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.profile}
          classes={{
            root: classes.link,
            divider: classes.divider,
            selected: classes.selectedLink,
          }}
          button
          divider
        >
          <ListItemIcon className={classes.icon}>
            <OpenInNew />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ className: classes.itemTextTypography }}>
            <span className={classes.orv4link}>app.objectrocket.com</span>
          </ListItemText>
        </ListItem>
      </SegmentClickTracker>
      {isAdmin && (
        <SegmentClickTracker buttonName="Impersonate(Nav Bar)">
          <ListItem
            className={classes.profile}
            component={NavLink}
            to="/mission-ctrl/org-impersonation"
            classes={{
              root: classes.link,
              divider: classes.divider,
              selected: classes.selectedLink,
            }}
            button
            divider
            selected={location.pathname === '/mission-ctrl/org-impersonation'}
          >
            <ListItemIcon className={classes.icon}>
              <Warning />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ className: classes.itemTextTypography }}>
              Impersonate an Org
            </ListItemText>
          </ListItem>
        </SegmentClickTracker>
      )}
      <SegmentClickTracker buttonName="Documentation (Nav Bar)">
        <ListItem
          component="a"
          classes={{
            root: classes.link,
            divider: classes.divider,
            selected: classes.selectedLink,
          }}
          href="https://docs.objectrocket.com"
          target="_blank"
          rel="noopener noreferrer"
          button
          divider
        >
          <ListItemIcon className={classes.icon}>
            <Description />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ className: classes.itemTextTypography }}>
            Documentation
          </ListItemText>
        </ListItem>
      </SegmentClickTracker>
      <SegmentClickTracker buttonName="Support (Nav Bar)">
        <ListItem
          component="a"
          classes={{
            root: classes.link,
            divider: classes.divider,
            selected: classes.selectedLink,
          }}
          href="https://objectrocket.zendesk.com/hc/en-us"
          target="_blank"
          rel="noopener noreferrer"
          button
          divider
        >
          <ListItemIcon className={classes.icon}>
            <Help />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ className: classes.itemTextTypography }}>
            Support
          </ListItemText>
        </ListItem>
      </SegmentClickTracker>
      {!isAuthenticated() ? (
        <ListItem
          component={NavLink}
          classes={{
            root: classes.link,
            divider: classes.divider,
            selected: classes.selectedLink,
          }}
          to={{
            pathname: '/login',
            state: { from: window.location.pathname },
          }}
          button
          divider
        >
          <ListItemIcon className={classes.icon}>
            <TransitEnterexit />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ className: classes.itemTextTypography }}>
            Log In
          </ListItemText>
        </ListItem>
      ) : (
        <SegmentClickTracker buttonName="Log Out (Nav Bar)">
          <ListItem
            component={NavLink}
            classes={{
              root: classes.link,
              divider: classes.divider,
              selected: classes.selectedLink,
            }}
            to={{
              pathname: '/logout',
              state: { from: window.location.pathname },
            }}
            button
            divider
          >
            <ListItemIcon className={classes.icon}>
              <ExitToApp />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ className: classes.itemTextTypography }}>
              Log Out
            </ListItemText>
          </ListItem>
        </SegmentClickTracker>
      )}
    </List>
  );
}

UnstyledNavItems.propTypes = propTypes;
UnstyledNavItems.defaultProps = defaultProps;

const styles = (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
  },
  link: {
    display: 'flex',
    minHeight: theme.spacing(5),
    justifyContent: 'center',
    flexDirection: 'row',
    width: '100%',
  },
  selectedLink: {
    backgroundColor: theme.customColors.mediumGrey,
    borderBottomColor: theme.customColors.teal,
  },
  icon: {
    marginRight: 0,
    /**
     * This should not be necessary. But either MUI4 has a default of min-width: 56px for
     * ListItemIcon, or there's some wierd magic inheritance going on.
     */
    minWidth: 'unset',
  },
  drawerPaper: {
    position: 'relative',
  },
  orange: {
    color: theme.customColors.orange,
  },
  itemTextTypography: {
    fontFamily: '"Libre Light", sans-serif',
    fontSize: '1rem',
    marginLeft: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    link: {
      minHeight: 50,
      flexDirection: 'row',
      justifyContent: 'flex-start',
    },
    badge: {
      top: 2,
    },
  },
  [theme.breakpoints.up('md')]: {
    profile: {
      display: 'none',
    },
    badge: {
      top: 2,
    },
    selected: {}, // Necessary to reference selectedLink within this media query
    divider: {
      '&:not($selectedLink)': {
        borderBottomColor: theme.customColors.mediumGrey,
      },
    },
  },
  orv4link: {
    color: theme.customColors.sky,
  },
});

export default withStyles(styles)(UnstyledNavItems);
